/** Abfragen und Aenderungen (TanStack Query) je Ressource der API. */

import { skipToken, useInfiniteQuery, useIsMutating, useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { offeneFrage, type Chat, type ChatDetail } from "./assistent";
import { ApiFehler, api, meldung, setzeCsrf, vorschlagGeaendert } from "./client";
import { mitGrenze, mitGrenzeErkannt } from "../design/sichern";
import { fassungZuruecksetzen } from "./fassung";
import { zurueckVergessen } from "./nachweis";
import { rueckfallZuruecksetzen } from "./freigabeRueckfall";
import { hinweisZuruecksetzen } from "./nutzungHinweis";
import { anbieterListeGueltig, imapTakt } from "./mailanbieter";
import { registriertVergessen } from "./registriert";
import { dateiname } from "../seiten/briefe/anschrift";
import { doppelteLesen } from "../seiten/zusammenlegen";
import type {
  AnbieterErgebnis,
  Anschrift,
  AnbieterStand,
  Absender,
  AbsenderRegel,
  Eingang,
  EingangMail,
  ImapKonto,
  Akte,
  Auftrag,
  Brief,
  Formularfeld,
  SignaturBefund,
  Unterschrift,
  Sichtbarkeit,
  Versand,
  Vorlage,
  Werkzeug,
  AkteDetail,
  Aufgabe,
  Bestaetigung,
  Dokument,
  DokumentAendern,
  DokumentDetail,
  Frist,
  FristAendern,
  FristNeu,
  Ich,
  Kategorie,
  Liste,
  Nutzungsbericht,
  RueckmeldungAntwort,
  RueckmeldungBetrieb,
  Person,
  Regel,
  Status,
  Stammdaten,
  Suchergebnis,
  Uebersicht,
  AuditSeite,
  Einladung,
  Export,
  Haushalt,
  Mitglied,
  PlanName,
  PlanStand,
  GelerntAbsender,
  Kennenlernen,
  ZusammenlegenAus,
  GeaendertFeld,
  Vorschlag,
  Erinnerung,
  FreigabeAus,
  FreigabeEinwilligung,
  FreigabeEintrag,
  FreigabeVorschau,
  FreigabeWahl,
  GaistVorschlag,
} from "./typen";

// ---------------------------------------------------------------- Sitzung

/** R1b W82 Web-6b: nach einem bestätigten Abmelden (auch Konto löschen) führt die Routen-Wache ohne Rückweg zur Anmeldung, nach
 *  dem Ablauf der Sitzung (401 einer Abfrage, `sitzung.ts`) mit Rückweg. Gilt, bis wieder eine Sitzung da ist. */
let bewusstAbgemeldet = false;
export const abmeldungBewusst = () => bewusstAbgemeldet;

/** `ich` laden; setzt das CSRF-Zeichen. `null` heisst: keine Sitzung (401), kein Fehler. */
async function ichLaden({ signal }: { signal?: AbortSignal }): Promise<Ich | null> {
  try {
    const ich = await api<Ich>("/auth/ich", { signal });
    setzeCsrf(ich.csrf);
    bewusstAbgemeldet = false;
    return ich;
  } catch (fehler) {
    if (fehler instanceof ApiFehler && fehler.status === 401) return null;
    throw fehler;
  }
}

/** Angemeldet? `null` heisst: keine Sitzung (401), kein Fehler. */
export function useIch() {
  return useQuery({
    queryKey: ["ich"],
    queryFn: ichLaden,
    staleTime: 60_000,
  });
}

function angemeldet(qc: QueryClient, ich: Ich) {
  setzeCsrf(ich.csrf);
  bewusstAbgemeldet = false;
  fassungZuruecksetzen(); // W17b: bei jeder Anmeldung wieder nach einer neuen Fassung fragen
  hinweisZuruecksetzen(); // W77: ebenso das Blatt zur Nutzungsstatistik, solange der Server es offen haelt
  rueckfallZuruecksetzen(); // W81 B5: ebenso das Blatt zur neuen Fassung der Einwilligung, solange die Marke steht
  registriertVergessen(); // W82 Web-5b (K4): nach der Anmeldung gilt der Merker „Konto angelegt, noch nicht bestätigt“ nicht mehr
  qc.clear();
  qc.setQueryData(["ich"], ich);
}

export function useAnmelden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (daten: { email: string; passwort: string }) =>
      api<Ich>("/auth/anmelden", { json: daten }),
    onSuccess: (ich) => angemeldet(qc, ich),
  });
}

/** W20 (D-063): Anmeldung per Link. Die Antwort ist immer dieselbe, ob es ein Konto gibt oder nicht. W74 (D-282):
 *  `client: "web"`, damit der Link aus der Mail im Browser oeffnet (`/anmelden/link/web`) und nicht in der App. */
export function useLinkAnfordern() {
  return useMutation({
    mutationFn: (email: string) => api<{ status: "mail_gesendet" }>("/auth/link", { json: { email, client: "web" } }),
  });
}

/** W20: den Link aus der Mail einloesen; danach eine Sitzung wie nach `anmelden`. */
export function useLinkEinloesen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api<Ich>("/auth/link-einloesen", { json: { token } }),
    onSuccess: (ich) => angemeldet(qc, ich),
  });
}

/** W77 (D-292): nach der Kontoanlage gilt die Wahl des Haekchens sofort, auch wenn ein Server vor W77 das Feld nicht
 *  kennt und `nutzung_aus` false meldet. Die Wahl steht im `ich` des Speichers, denn die Statistik folgt jedem Ereignis
 *  dieser Abfrage (main.tsx, auch dem Einhaengen einer Seite); das naechste `ich` vom Server gilt dann wie bisher. */
function mitWahl(ich: Ich, aus: boolean | undefined): Ich {
  return aus && !ich.nutzer.nutzung_aus ? { ...ich, nutzer: { ...ich.nutzer, nutzung_aus: true } } : ich;
}

function passwortStand(qc: QueryClient, hat: boolean) {
  // festlegen geht danach nicht mehr (hat eins) oder erst wieder mit frischem Link (entfernt per Passwort)
  qc.setQueryData<Ich | null>(["ich"], (ich) => (ich ? { ...ich, hat_passwort: hat, passwort_setzen_moeglich: false } : ich));
}

/** W20: Passwort festlegen, solange das Konto keins hat; andere Sitzungen enden. */
export function usePasswortSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ passwort, signal }: { passwort: string; signal?: AbortSignal }) => api<undefined>("/auth/passwort-setzen", { json: { passwort }, signal }),
    onSuccess: () => passwortStand(qc, true),
  });
}

/** Neues Passwort mit dem alten bestaetigt; andere Sitzungen enden. */
export function usePasswortAendern() {
  return useMutation({
    mutationFn: ({ signal, ...daten }: { alt: string; neu: string; signal?: AbortSignal }) => api<undefined>("/auth/passwort", { json: daten, signal }),
  });
}

/** W20: Passwort entfernen, danach nur noch Anmeldung per Link; andere Sitzungen enden. W20b: nur mit
 *  dem Passwort; falsch beantwortet der Server seit W20d mit 403, das bleibt eine Meldung im Blatt. */
export function usePasswortEntfernen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ passwort, signal }: { passwort: string; signal?: AbortSignal }) => api<undefined>("/auth/passwort", { methode: "DELETE", json: { passwort }, signal }),
    onSuccess: () => passwortStand(qc, false),
  });
}

/** W17b: Zustimmung zu einer neuen Fassung der Rechtstexte; die Antwort ist das neue „ich“. */
export function useEinwilligungErneuern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (daten: { einwilligung: true; fassung: string }) => api<Ich>("/auth/einwilligung", { json: daten }),
    onSuccess: (ich) => {
      setzeCsrf(ich.csrf);
      bewusstAbgemeldet = false;
      qc.setQueryData(["ich"], ich);
    },
  });
}

/** W13b: neutral, immer „Mail geschickt“, ohne Sitzung; danach meldet man sich an. Ohne Passwort
 *  (W20) faellt das Feld weg; angemeldet wird dann per Link. */
export function useRegistrieren() {
  return useMutation({
    mutationFn: (daten: {
      email: string;
      passwort?: string;
      name: string;
      haushalt: string;
      einladungscode: string;
      einwilligung: boolean;
      fassung: string;
      nutzung_aus?: boolean;
    }) =>
      api<{ status: "mail_gesendet" }>("/auth/registrieren", { json: daten }),
  });
}

// ---------------------------------------------------------------- W68: Apple und Google

/** Eingerichtete Wege, ohne Anmeldung. Ohne Antwort (Server vor W68, Netz) zeigt die App keine Knoepfe. */
export function useAnbieterStand() {
  return useQuery({
    queryKey: ["anbieter"],
    queryFn: ({ signal }) => api<AnbieterStand>("/auth/anbieter", { signal }),
    staleTime: 5 * 60_000,
  });
}

/** Den Rueckweg vom Anbieter einloesen (genau einmal je state, AnbieterRueckweg). Mit `ich` eine Sitzung wie nach
 *  `anmelden`. */
export function useAnbieterEinloesen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (daten: { state: string; code?: string }) => api<AnbieterErgebnis>("/auth/anbieter/einloesen", { json: daten }),
    onSuccess: (e) => {
      if (e.ich) angemeldet(qc, e.ich);
    },
  });
}

/** Konto mit dem Ticket aus `einloesen` anlegen; danach angemeldet (F68-6 A). */
export function useAnbieterRegistrieren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (daten: { ticket: string; name: string; haushalt: string; einladungscode: string; einwilligung: true; fassung: string; nutzung_aus?: boolean }) =>
      api<Ich>("/auth/anbieter/registrieren", { json: daten }),
    onSuccess: (ich, daten) => angemeldet(qc, mitWahl(ich, daten.nutzung_aus)),
  });
}

/** Verbindung loesen, mit Passwort oder frischem Nachweis (F68-10 A: nicht bei der Weiterleitungsadresse). Dieselbe
 *  Sitzung und keine neue Seite: nur „ich“ neu wie bei der neuen Fassung, nicht `angemeldet` (dessen `qc.clear()` haengt
 *  die offene Seite von ihrer Abfrage ab, Daten und Konto zeigte dann weiter „Verbunden“). */
export function useAnbieterTrennen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: { anbieter: "apple" | "google"; passwort?: string; signal?: AbortSignal }) => api<Ich>("/auth/anbieter/trennen", { json: daten, signal }),
    onSuccess: (ich) => {
      setzeCsrf(ich.csrf);
      bewusstAbgemeldet = false;
      qc.setQueryData(["ich"], ich);
    },
  });
}

/** W82 Web-6b (WN-31, DD-01): 401 auf das Abmelden heißt „schon abgemeldet“ (die Sitzung gilt beim Server nicht mehr). */
export const schonAbgemeldet = (fehler: unknown) => fehler instanceof ApiFehler && fehler.status === 401;

/** W82 Web-6b (WN-31, DD-01): abgemeldet nur bei bestätigter Serverabmeldung (2xx) oder „schon abgemeldet“ (401); ohne Netz oder
 *  bei 5xx bleibt die Sitzung bestehen (das HttpOnly-Cookie kann nur der Server löschen), der Fehler steht am Knopf. Der gemerkte
 *  Rückweg (W20g) geht in jedem Fall. */
export function useAbmelden() {
  const qc = useQueryClient();
  const abmelden = useMutation({
    mutationKey: ABMELDEN,
    mutationFn: async () => {
      try {
        return await api<undefined>("/auth/abmelden", { methode: "POST" });
      } catch (fehler) {
        if (!(fehler instanceof ApiFehler) || fehler.status !== 403) throw fehler;
        // W82 Web-11a W11A-1 (WN-49, D-1754): 403 heißt hier ein altes CSRF-Zeichen; `ich` neu (setzt das Zeichen der Sitzung),
        // dann genau ein zweiter Versuch, dessen Ausgang gilt (WN-31). Scheitert `ich` selbst, geht kein zweiter Versuch. Antwortet
        // `ich` mit 401, bleibt der Speicher, wie er ist, und der zweite Versuch entscheidet (P-10).
        const ich = await ichLaden({});
        if (ich) qc.setQueryData(["ich"], ich);
        return await api<undefined>("/auth/abmelden", { methode: "POST" });
      }
    },
    onSuccess: () => abgemeldet(qc),
    onError: (fehler) => {
      if (schonAbgemeldet(fehler)) abgemeldet(qc);
    },
    onSettled: () => zurueckVergessen(),
  });
  // W82 Web-11a W11A-1 (WN-48, D-1754): eine Abmeldung zugleich über alle Wege (Knopf der Einstellungen, Toast nach der
  // Link-Anmeldung, auch während er ausblendet, und seine Wiederholung); die Sperre greift ab dem Aufruf, nicht erst mit dem
  // nächsten Rendern, und fällt mit jedem Ausgang
  const laeuft = useIsMutating({ mutationKey: ABMELDEN }) > 0;
  const starten = () => (qc.isMutating({ mutationKey: ABMELDEN }) > 0 ? null : abmelden.mutateAsync());
  return { ...abmelden, laeuft, starten };
}
const ABMELDEN = ["abmelden"];

// ---------------------------------------------------------------- Stammdaten

export function usePersonen() {
  return useQuery({
    queryKey: ["personen"],
    queryFn: ({ signal }) => api<Person[]>("/personen", { signal }),
    staleTime: 60_000,
  });
}

export function usePersonAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, signal }: { name: string; signal?: AbortSignal }) => api<Person>("/personen", { json: { name }, signal }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["personen"] }),
  });
}

/** W75 (D-287): Name und Anschrift einer Person (`PATCH /api/personen/{id}`); die Anschrift einer Person mit eigenem
 *  Konto aendert nur dieses Konto (403). */
export function usePersonAendern(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: { name?: string; anschrift?: Anschrift; signal?: AbortSignal }) => api<Person>(`/personen/${id}`, { methode: "PATCH", json: daten, signal }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["personen"] }),
  });
}

/** W75 (D-287): „Meine Anschrift“ (`PUT /api/konto/anschrift`, ersetzt alle sieben Felder der eigenen Person). */
export function useKontoAnschrift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: Anschrift & { signal?: AbortSignal }) => api<Person>("/konto/anschrift", { methode: "PUT", json: daten, signal }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["personen"] }),
  });
}

export function useKategorien() {
  return useQuery({
    queryKey: ["kategorien"],
    queryFn: ({ signal }) => api<Kategorie[]>("/kategorien", { signal }),
    staleTime: 60_000,
  });
}

export function useAbsender() {
  return useQuery({
    queryKey: ["absender"],
    queryFn: ({ signal }) => api<Absender[]>("/absender", { signal }),
    staleTime: 30_000,
  });
}

/** W47f (Plan E7): gleich aussehende Absender als Vorschlag. Ein Fehler zeigt nichts an (F-W1), darum ohne Wiederholung. */
export function useDoppelteAbsender() {
  return useQuery({
    queryKey: ["absender-doppelte"],
    queryFn: async ({ signal }) => doppelteLesen(await api<unknown>("/absender/doppelte", { signal })),
    staleTime: 30_000,
    retry: false,
  });
}

/** W47f (Plan E6): Vorschau des Zusammenlegens ueber `probe` (schreibt nie, W47b P7). Der Schluessel traegt beide IDs:
 *  wechselt das Paar, ist die alte Vorschau sofort weg, eine verspaetete Antwort des alten Paars erscheint nie. */
export function useZusammenlegenVorschau(zielId: string | undefined, quelleId: string | undefined) {
  return useQuery({
    queryKey: ["absender-vorschau", zielId, quelleId],
    queryFn: ({ signal }) =>
      api<ZusammenlegenAus>(`/absender/${zielId}/zusammenlegen`, { json: { quelle_id: quelleId, probe: true }, signal }),
    enabled: Boolean(zielId && quelleId && zielId !== quelleId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** Endgueltig (F2 A). Danach alles neu, was Absender kennt, dazu Vorschlaege und Gelerntes; bei 404 (ein Glied ist
 *  inzwischen weg) die Absender und Vorschlaege. */
export function useZusammenlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["absender-zusammenlegen"],
    mutationFn: (p: { zielId: string; quelleId: string; signal?: AbortSignal }) =>
      api<ZusammenlegenAus>(`/absender/${p.zielId}/zusammenlegen`, { json: { quelle_id: p.quelleId, probe: false }, signal: p.signal }),
    onSuccess: () => {
      dokumenteNeuLaden(qc);
      void qc.invalidateQueries({ queryKey: ["absender-doppelte"] });
      void qc.invalidateQueries({ queryKey: ["gelernt"] });
    },
    onError: (fehler) => {
      if (fehler instanceof ApiFehler && fehler.status === 404) {
        void qc.invalidateQueries({ queryKey: ["absender"] });
        void qc.invalidateQueries({ queryKey: ["absender-doppelte"] });
      }
    },
  });
}

/** W47b-2 (Entscheid F1 = B): „Sind verschieden“ gilt im ganzen Haushalt, auf allen Geraeten. */
export function useAbsenderVerschieden() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["absender-verschieden"],
    mutationFn: ({ absender_ids, signal }: { absender_ids: string[]; signal?: AbortSignal }) => api<undefined>("/absender/verschieden", { json: { absender_ids }, signal }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["absender-doppelte"] }),
  });
}

/** „Ausgeblendete Vorschlaege wieder zeigen“: die ausgeblendeten Gruppen laden und je Gruppe die Paare aufheben. */
export function useAusgeblendeteZeigen() {
  const qc = useQueryClient();
  return useMutation({
    // W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`) für den ganzen Weg, ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: () =>
      mitGrenze(async (signal) => {
        const alle = doppelteLesen(await api<unknown>("/absender/doppelte?mit_ausgeblendeten=true", { signal })).gruppen;
        for (const g of alle.filter((x) => x.verschieden === true)) {
          await api<undefined>("/absender/verschieden", { methode: "DELETE", json: { absender_ids: g.absender.map((a) => a.id) }, signal });
        }
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["absender-doppelte"] }),
  });
}

// ---------------------------------------------------------------- Dokumente

export type Filter = {
  status?: Status;
  person_id?: string;
  kategorie_id?: string;
  jahr?: number;
  akte_id?: string;
  absender_id?: string;
  sortierung?: "eingang" | "datum";
  pro_seite?: number;
};

function query(filter: Record<string, string | number | undefined>): string {
  const teile = Object.entries(filter).filter(([, w]) => w !== undefined && w !== "");
  return teile.length ? `?${new URLSearchParams(teile.map(([k, w]) => [k, String(w)]))}` : "";
}

/** Solange etwas in Verarbeitung ist, alle 3 s neu laden (der Worker arbeitet im Hintergrund). */
function nochInArbeit(liste?: { eintraege: Dokument[] }): number | false {
  // W82 Web-Empfänger (E4): eine Antwort ohne `eintraege` (Attrappe) laesst die Liste leer, statt die Ansicht zu werfen
  return liste?.eintraege?.some((d) => d.status === "eingang" || d.status === "verarbeitung")
    ? 3000
    : false;
}

export function useDokumente(filter: Filter = {}, aktiv = true) {
  const qc = useQueryClient();
  const ergebnis = useQuery({
    queryKey: ["dokumente", filter],
    enabled: aktiv,
    queryFn: ({ signal }) => api<Liste<Dokument>>(`/dokumente${query(filter)}`, { signal }),
    refetchInterval: (q) => nochInArbeit(q.state.data),
  });
  // Ist ein Brief fertig erkannt, wechselt er die Liste (etwa nach „Bitte pruefen“) und kann
  // Fristen und Akten mitbringen: dann alle Listen neu laden, nicht nur die, die gerade pollt.
  const inArbeit = ergebnis.data?.eintraege?.filter((d) => d.status === "eingang" || d.status === "verarbeitung").length ?? 0;
  const vorher = useRef(inArbeit);
  useEffect(() => {
    if (inArbeit < vorher.current) {
      for (const schluessel of ["dokumente", "fristen", "akten"]) void qc.invalidateQueries({ queryKey: [schluessel] });
    }
    vorher.current = inArbeit;
  }, [inArbeit, qc]);
  return ergebnis;
}

/** Alle geladenen Seiten als eine Liste, jeder Brief einmal: die API blaettert per Versatz, kommt
 *  oben ein Brief dazu, steht der letzte der vorigen Seite auf der naechsten noch einmal
 *  (Verifier W4 Runde 2, F-D1). */
export function alleEintraege(seiten: { pages: Liste<Dokument>[] } | undefined): Dokument[] {
  const gesehen = new Set<string>();
  return (seiten?.pages ?? []).flatMap((s) => s.eintraege).filter((d) => !gesehen.has(d.id) && Boolean(gesehen.add(d.id)));
}

/** Seitenweise laden („Weitere laden“), statt bei 100 oder 200 Briefen still abzuschneiden (F13). */
export function useDokumenteSeiten(filter: Filter, proSeite = 50) {
  return useInfiniteQuery({
    queryKey: ["dokumente", "seiten", filter, proSeite],
    queryFn: ({ signal, pageParam }) =>
      api<Liste<Dokument>>(`/dokumente${query({ ...filter, pro_seite: proSeite, seite: pageParam })}`, { signal }),
    initialPageParam: 1,
    getNextPageParam: (letzte) => (letzte.seite * letzte.pro_seite < letzte.gesamt ? letzte.seite + 1 : undefined),
    refetchInterval: (q) => nochInArbeit({ eintraege: q.state.data?.pages.flatMap((s) => s.eintraege) ?? [] }),
  });
}

/** W58-Web R1/R2: der Server hat endgueltig abgelehnt: Sitzung weg (401), verboten (403), Brief nicht da (404, etwa
 *  anderswo geloescht) oder entfernt (410). Andere 4xx sind voruebergehend und lassen das Nachladen weiterlaufen,
 *  vor allem 429 (Obergrenze je Konto in `angemeldet`, Server), aber auch 408 und 409. */
export const abgewiesen = (fehler: unknown) => fehler instanceof ApiFehler && [401, 403, 404, 410].includes(fehler.status);

/** `nachladen: false` (W37i-Web): liegt der Brief schon im Cache, beim Einhaengen nicht noch einmal abrufen. */
export function useDokument(id: string | undefined, nachladen = true) {
  return useQuery({
    queryKey: ["dokument", id],
    queryFn: ({ signal }) => api<DokumentDetail>(`/dokumente/${id}`, { signal }),
    enabled: Boolean(id),
    refetchOnMount: nachladen,
    // W58-Web R1/R2: nach einer endgueltigen Ablehnung (`abgewiesen`) nicht weiter nachladen; der alte Stand bleibt in
    // `data` und liesse sonst alle 2 s weiter fragen (404 nach `verarbeitung`); bei 429 und 5xx laeuft es weiter
    refetchInterval: (q) => {
      if (abgewiesen(q.state.error)) return false;
      // R3: schon der erste Abruf kann an der Obergrenze scheitern (429, `retry` in sitzung.ts wiederholt 4xx nicht);
      // ohne Stand in `data` fragte sonst nichts mehr nach und die Zeile bliebe bei „Wird erkannt“ stehen
      if (!q.state.data) return q.state.error instanceof ApiFehler && q.state.error.status === 429 ? 2000 : false;
      return ["eingang", "verarbeitung"].includes(q.state.data.status) ? 2000 : false;
    },
  });
}

/** W58-Web: Stand mehrerer Briefe, wie ihn `useDokument` in den Zeilen laedt. Liest nur mit (`enabled: false`),
 *  ruft selbst nicht ab; noch nicht geladen heisst `undefined`, abgewiesen (4xx, R1) `null`. */
export function useDokumentStaende(ids: string[]): (Status | null | undefined)[] {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ["dokument", id],
      queryFn: ({ signal }: { signal: AbortSignal }) => api<DokumentDetail>(`/dokumente/${id}`, { signal }),
      enabled: false,
    })),
    combine: (ergebnisse) => ergebnisse.map((q) => (abgewiesen(q.error) ? null : q.data?.status)),
  });
}

export function dokumenteNeuLaden(qc: ReturnType<typeof useQueryClient>) {
  for (const schluessel of ["dokumente", "dokument", "fristen", "akten", "akte", "regeln", "absender", "suche"]) {
    void qc.invalidateQueries({ queryKey: [schluessel] });
  }
}

/** `kontingent`: eine Grenze des Plans ist erreicht (402, W9). */
export type Hochgeladen = { dokument?: Dokument; dublette?: string; fehler?: string; kontingent?: boolean; name: string };

export function useHochladen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dateien: File[]): Promise<Hochgeladen[]> => {
      const ergebnisse: Hochgeladen[] = [];
      for (const datei of dateien) {
        const formular = new FormData();
        formular.append("datei", datei);
        try {
          ergebnisse.push({ name: datei.name, dokument: await api<Dokument>("/dokumente", { formular }) });
        } catch (fehler) {
          if (fehler instanceof ApiFehler && fehler.status === 409) {
            const id = (fehler.daten as { dokument_id?: string } | null)?.dokument_id;
            ergebnisse.push({ name: datei.name, dublette: id });
          } else {
            ergebnisse.push({
              name: datei.name,
              fehler: fehler instanceof Error ? fehler.message : String(fehler),
              kontingent: fehler instanceof ApiFehler && fehler.status === 402,
            });
          }
        }
      }
      return ergebnisse;
    },
    onSuccess: () => dokumenteNeuLaden(qc),
  });
}

/** W47i (WI6): der neue Stand eines Briefs nach einem 409 `VORSCHLAG_GEAENDERT`, gemeinsam fuer Karte und Blatt
 *  (Schluessel `["gezeigt", id]`). `nr` waechst mit jedem 409; wer einhaengt, merkt sich die Nummer, die schon da ist (deren
 *  Vorschlag steht dann schon im Detail) und uebernimmt nur spaetere. `vorher`: der Vorschlag, gegen den der Server
 *  verglichen hat (fuer die Zeilen der geaenderten Werte). */
export type Gezeigt = {
  nr: number;
  vorschlag_id: string;
  vorschlag: Vorschlag | null;
  vorher: Vorschlag | null;
  geaendert: GeaendertFeld[] | null;
  detail: string;
};
let gezeigtNr = 0;

/** Ablegen (Formular oder „Passt“). Laedt auch die Start-Runde neu: Kennenlernen zeigt dieselbe
 *  Vorschlagskarte (F14). `verzoegert`: erst nach so vielen ms neu laden, damit die Karte kurz
 *  „Abgelegt in …“ zeigt, bevor die naechste nachrueckt (Plan 6.1). W47i: `vorher` ist der Vorschlag, den Karte oder
 *  Blatt gerade zeigen; bei 409 `VORSCHLAG_GEAENDERT` schreibt der Haken an einer Stelle den neuen Vorschlag ins Detail
 *  und den gemeinsamen Stand (`useGezeigt`), auch wenn das Blatt schon weg ist (WI6, WI8). */
export function useBestaetigen(id: string, verzoegert = 0, vorher?: Vorschlag | null) {
  const qc = useQueryClient();
  return useMutation({
    // W82 Web-5f (BS30): `signal` aus `useSichern` bricht nach der Zeitgrenze ab; es geht nicht in den Körper
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: ({ signal, ...daten }: Bestaetigung & { signal?: AbortSignal }) =>
      mitGrenze((grenze) => api<Dokument>(`/dokumente/${id}/bestaetigen`, { json: daten, signal: grenze }), signal),
    onSuccess: () => {
      if (verzoegert > 0) setTimeout(() => kennenlernenNeuLaden(qc), verzoegert);
      else kennenlernenNeuLaden(qc);
    },
    onError: (fehler) => {
      const g = vorschlagGeaendert(fehler);
      if (!g) return;
      qc.setQueryData<DokumentDetail>(["dokument", id], (alt) => alt && { ...alt, vorschlag: g.vorschlag, vorschlag_id: g.vorschlag_id });
      gezeigtNr += 1;
      qc.setQueryData<Gezeigt>(["gezeigt", id], {
        nr: gezeigtNr,
        vorschlag_id: g.vorschlag_id,
        vorschlag: g.vorschlag,
        vorher: vorher ?? null,
        geaendert: g.geaendert,
        detail: g.detail,
      });
      void qc.invalidateQueries({ queryKey: ["dokumente"] });
    },
  });
}

/** W47i (WI6): der gemeinsame Stand nach dem letzten 409 `VORSCHLAG_GEAENDERT` dieses Briefs, sonst undefined. Nur
 *  aus dem Cache, nie abgerufen. */
export function useGezeigt(id: string): Gezeigt | undefined {
  return useQuery<Gezeigt>({ queryKey: ["gezeigt", id], queryFn: skipToken, staleTime: Infinity, gcTime: Infinity }).data;
}

/** W15 Regelvorschlag: die noch nicht aktive Regel zum Absender eines gerade abgelegten Briefs
 *  (oder null). Ein Fehler hier darf das Ablegen nie stoeren. */
export async function regelVorschlag(d: Dokument): Promise<Regel | null> {
  if (!d.absender_id || !d.kategorie_id) return null;
  try {
    const regeln = await api<Regel[]>(`/regeln${query({ absender_id: d.absender_id })}`);
    return regeln.find((r) => !r.aktiv && r.kategorie_id === d.kategorie_id) ?? null;
  } catch {
    return null;
  }
}

export function useRegelAktivieren() {
  const qc = useQueryClient();
  return useMutation({
    // mit dem gezeigten Stand: meint die Regel inzwischen etwas anderes, lehnt der Server ab (409)
    mutationFn: (r: Regel) => api<Regel>(`/regeln/${r.id}/aktivieren`, { json: { kategorie_id: r.kategorie_id, person_id: r.person_id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regeln"] }),
  });
}

/** W47e (Plan E3): Angaben eines abgelegten Briefs nachtraeglich, ein PATCH mit nur den geaenderten Feldern (Server
 *  W47a). Danach alles, was der Brief beruehrt, neu laden, dazu die Finanzen: mit einer Akte kann der Server die Rate
 *  als Beitrag eintragen (Weg C). */
export function useDokumentAendern(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: DokumentAendern & { signal?: AbortSignal }) => api<Dokument>(`/dokumente/${id}`, { methode: "PATCH", json: daten, signal }),
    onSuccess: () => {
      dokumenteNeuLaden(qc);
      void qc.invalidateQueries({ queryKey: ["finanzen"] });
    },
  });
}

/** W81: nach Freigeben und Zurueckziehen der Brief (Menue, leise Zeile), die Liste und `ich` (Zeile in den Einstellungen). */
function freigabeNeuLaden(qc: QueryClient, id: string) {
  for (const schluessel of [["dokument", id], ["freigaben"], ["ich"]]) void qc.invalidateQueries({ queryKey: schluessel });
}

/** W81 (D-331): Vorschau dessen, was der Server bei einer Freigabe bekaeme; die Begriffe schwaerzt nur der Server. */
export function useFreigabeVorschau(id: string) {
  return useMutation({
    mutationFn: (begriffe: string[]) => api<FreigabeVorschau>(`/dokumente/${id}/freigabe/vorschau`, { methode: "POST", json: { begriffe } }),
  });
}

/** W81: genau die gezeigte Vorschau freigeben (Fassung, Begriffe, Kennung der Vorschau). W92 (Nachtrag D-1354): dazu optional
 *  die Notiz zum Brief als `hinweis` (Server W92-S, hoechstens 500 Zeichen). */
export function useFreigeben(id: string) {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: (daten: { fassung: string; begriffe: string[]; kennung: string; hinweis?: string }) =>
      mitGrenze((signal) => api<FreigabeAus>(`/dokumente/${id}/freigabe`, { methode: "POST", json: daten, signal })),
    onSuccess: () => freigabeNeuLaden(qc, id),
  });
}

/** W81: Freigabe eines Briefs zurueckziehen (Menue „Mehr“ und Liste), ohne Rueckfrage. */
export function useFreigabeZurueckziehen() {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: (dokumentId: string) => mitGrenze((signal) => api<undefined>(`/dokumente/${dokumentId}/freigabe`, { methode: "DELETE", signal })),
    onSuccess: (_, dokumentId) => {
      // die Zeile geht sofort, nicht erst mit dem naechsten Abruf; ebenso „Freigabe zurückziehen“ im Menue des Briefs. Gesperrt
      // wie beim Server nach dem Widerruf (W81-S-5), damit „Zur Verbesserung freigeben“ unter „alle“ und „nie“ sofort da ist
      // (R1 O1); ein Server ohne das Feld bekommt es auch hier nicht
      qc.setQueryData<FreigabeEintrag[]>(["freigaben"], (alt) => alt?.filter((f) => f.dokument_id !== dokumentId));
      qc.setQueryData<DokumentDetail>(["dokument", dokumentId], (alt) =>
        alt?.freigabe
          ? { ...alt, freigabe: { ...alt.freigabe, erteilt: null, ...(alt.freigabe.gesperrt === undefined ? {} : { gesperrt: true }) } }
          : alt,
      );
      freigabeNeuLaden(qc, dokumentId);
    },
  });
}

/** W81: eigene Freigaben, neueste zuerst. Ein Server ohne diese Route (404) hat keine: leere Liste, kein Fehler. */
export function useFreigaben() {
  return useQuery({
    queryKey: ["freigaben"],
    queryFn: async ({ signal }) => {
      try {
        return await api<FreigabeEintrag[]>("/freigaben", { signal });
      } catch (fehler) {
        if (fehler instanceof ApiFehler && fehler.status === 404) return [];
        throw fehler;
      }
    },
  });
}

/** W81 (Nachtrag D-433): Einwilligungstext ohne Brief fuer „Alle meine Briefe“, nur solange das Blatt offen ist. */
export function useFreigabeEinwilligung(aktiv: boolean) {
  return useQuery({
    queryKey: ["freigabe-einwilligung"],
    queryFn: ({ signal }) => api<FreigabeEinwilligung>("/freigaben/einwilligung", { signal }),
    enabled: aktiv,
    staleTime: 0,
    gcTime: 0,
  });
}

/** W81 (Nachtrag D-433): Wahl des Kontos setzen; bei `alle` mit der Fassung der gezeigten Einwilligung, sonst null. Jede
 *  gesicherte Wahl loescht beim Server die Marke der neuen Fassung (W81 B5), der Cache folgt sofort. */
export function useFreigabeWahl() {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: (daten: { wahl: FreigabeWahl; fassung: string | number | null }) => mitGrenze((signal) => api<undefined>("/freigaben/wahl", { methode: "PUT", json: daten, signal })),
    onSuccess: (_, daten) => {
      qc.setQueryData<Ich | null>(["ich"], (ich) =>
        ich
          ? { ...ich, brief_freigabe_wahl: daten.wahl, ...(ich.brief_freigabe_neu_einwilligen === undefined ? {} : { brief_freigabe_neu_einwilligen: false }) }
          : ich,
      );
      void qc.invalidateQueries({ queryKey: ["ich"] });
    },
  });
}

export function useNeuVerarbeiten(id: string) {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: (ganz: boolean) =>
      mitGrenze((signal) => api<Dokument>(`/dokumente/${id}/verarbeiten${ganz ? "?ganz=true" : ""}`, { methode: "POST", signal })),
    onSuccess: () => dokumenteNeuLaden(qc),
  });
}

export function useLoeschen(id: string) {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: () => mitGrenze((signal) => api<undefined>(`/dokumente/${id}`, { methode: "DELETE", signal })),
    onSuccess: () => dokumenteNeuLaden(qc),
  });
}

/** Seitenweise wie die Dokumentliste (W56, PG-FE-001); Platzhalter nur aus derselben Anfrage, damit eine neue
 *  Suche nie die Seiten der alten zeigt oder an sie anhaengt. */
export function useSuche(q: string) {
  return useInfiniteQuery({
    queryKey: ["suche", q],
    queryFn: ({ signal, pageParam }) => api<Suchergebnis>(`/suche${query({ q, seite: pageParam })}`, { signal }),
    initialPageParam: 1,
    getNextPageParam: (letzte) => (letzte.seite * letzte.pro_seite < letzte.gesamt ? letzte.seite + 1 : undefined),
    enabled: q.trim().length >= 2,
    placeholderData: (vorher, vorherige) => (vorherige?.queryKey[1] === q ? vorher : undefined),
  });
}

// ---------------------------------------------------------------- Akten, Fristen, Aufgaben

export function useAkten() {
  return useQuery({
    queryKey: ["akten"],
    queryFn: ({ signal }) => api<Akte[]>("/akten", { signal }),
  });
}

export function useAkte(id: string | undefined) {
  return useQuery({
    queryKey: ["akte", id],
    queryFn: ({ signal }) => api<AkteDetail>(`/akten/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

/** Nach Aenderungen an Akten: Liste, Detail und Finanzueberblick neu laden. */
function aktenNeu(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["akten"] });
  void qc.invalidateQueries({ queryKey: ["akte"] });
  void qc.invalidateQueries({ queryKey: ["finanzen"] });
}

export function useAkteAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: { art: string; name: string; stammdaten?: Stammdaten; signal?: AbortSignal }) => api<Akte>("/akten", { json: daten, signal }),
    onSuccess: () => aktenNeu(qc),
  });
}

export function useAkteAendern(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: { name?: string; art?: string; status?: string; stammdaten?: Stammdaten; signal?: AbortSignal }) =>
      api<Akte>(`/akten/${id}`, { methode: "PATCH", json: daten, signal }),
    onSuccess: () => aktenNeu(qc),
  });
}

// ---------------------------------------------------------------- Finanzen (W6)

export function useFinanzen() {
  return useQuery({
    queryKey: ["finanzen"],
    queryFn: ({ signal }) => api<Uebersicht>("/finanzen", { signal }),
  });
}

export function useHinweisAusblenden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (h: { schluessel: string; bezug: string }) => api<unknown>("/finanzen/hinweise/ausblenden", { json: h }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finanzen"] }),
  });
}

export function useHinweiseZeigen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<unknown>("/finanzen/hinweise/ausgeblendet", { methode: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finanzen"] }),
  });
}

export function useErinnerungSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (erinnerung_tage: number) => api<Ich>("/auth/ich", { methode: "PATCH", json: { erinnerung_tage } }),
    onSuccess: (ich) => qc.setQueryData(["ich"], ich),
  });
}

/** `aktiv` falsch: nicht abfragen, nur lesen, was schon im Speicher liegt (Blatt „Frist“ außerhalb des Reiters, W82 Web-5h1). */
export function useFristen(offen = true, aktiv = true) {
  return useQuery({
    queryKey: ["fristen", offen],
    queryFn: ({ signal }) => api<Frist[]>(`/fristen${query({ offen: String(offen) })}`, { signal }),
    enabled: aktiv,
  });
}

export function useFristErledigt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, erledigt }: { id: string; erledigt: boolean }) =>
      api<Frist>(`/fristen/${id}`, { methode: "PATCH", json: { erledigt } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["fristen"] });
      void qc.invalidateQueries({ queryKey: ["akte"] });
    },
  });
}

/** W82 Web-5c (K15): Frist von Hand (`POST /api/fristen`, 201 mit `FristAus`). Danach Fristen, Akten und Akte neu; die
 *  Lage der Startseite rechnet aus den Fristen. Der Rückruf wartet auf das Neuladen, damit die neue Zeile steht, wenn das
 *  Blatt schließt. */
export function useFristAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: FristNeu & { signal?: AbortSignal }) => api<Frist>("/fristen", { json: daten, signal }),
    onSuccess: () => Promise.all(["fristen", "akten", "akte"].map((schluessel) => qc.invalidateQueries({ queryKey: [schluessel] }))),
  });
}

/** W82 Web-5h1: nach Ändern oder Löschen einer Frist Fristen, Akten, Akte, Finanzen und Brief neu (die Detailantwort des Briefs
 *  trägt den Vorschlag ohne gelöschte Frist, Server 5h-S2); nach dem Löschen auch die Aufgaben (ihre Frist fällt weg). Der Rückruf
 *  wartet auf das Neuladen, damit die Liste steht, wenn das Blatt schließt. */
function fristFolgen(qc: ReturnType<typeof useQueryClient>, aufgaben = false) {
  const schluessel = ["fristen", "akten", "akte", "finanzen", "dokument", ...(aufgaben ? ["aufgaben"] : [])];
  return Promise.all(schluessel.map((s) => qc.invalidateQueries({ queryKey: [s] })));
}

/** W82 Web-5h1 (K45, Server 5h-S und 5h-S2): `PATCH /api/fristen/{id}` mit den geänderten Feldern, 200 mit `FristAus`. */
export function useFristAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, signal, ...daten }: FristAendern & { id: string; signal?: AbortSignal }) =>
      api<Frist>(`/fristen/${id}`, { methode: "PATCH", json: daten, signal }),
    onSuccess: () => fristFolgen(qc),
  });
}

/** W82 Web-5h1 (K45, Server 5h-S2: jede Frist des Haushalts): `DELETE /api/fristen/{id}`, 204. 404 (schon gelöscht, etwa auf
 *  einem anderen Gerät) gilt als erledigt: kein Fehler, die Listen laden neu. */
export function useFristLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, signal }: { id: string; signal?: AbortSignal }) => {
      try {
        await api<undefined>(`/fristen/${id}`, { methode: "DELETE", signal });
      } catch (e) {
        if (!(e instanceof ApiFehler && e.status === 404)) throw e;
      }
    },
    onSuccess: () => fristFolgen(qc, true),
  });
}

export function useAufgaben() {
  return useQuery({
    queryKey: ["aufgaben"],
    queryFn: ({ signal }) => api<Aufgabe[]>("/aufgaben", { signal }),
  });
}

export function useAufgabeAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: { titel: string; faellig: string | null; signal?: AbortSignal }) => api<Aufgabe>("/aufgaben", { json: daten, signal }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aufgaben"] }),
  });
}

export function useAufgabeErledigt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, erledigt }: { id: string; erledigt: boolean }) =>
      api<Aufgabe>(`/aufgaben/${id}`, { methode: "PATCH", json: { erledigt } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aufgaben"] }),
  });
}

// ---------------------------------------------------------------- Gaist (W5)

export function useChats() {
  return useQuery({
    queryKey: ["chats"],
    queryFn: ({ signal }) => api<Chat[]>("/assistent/chats", { signal }),
  });
}

/** nachladen: entsteht die Antwort auf die letzte Frage noch auf dem Server (Seite zwischendurch
 *  verlassen), holt die Seite den Chat alle 3 s, bis der Server fertig ist. abrufen false (W73): nur der
 *  Stand im Cache, kein Abruf; so zeigt der neue Chat waehrend der ersten Antwort, was die Seite selbst
 *  eingetragen hat, und der Server-Stand mit der offenen Frage kommt nicht doppelt dazu. */
export function useChat(id: string | undefined, nachladen = false, abrufen = true) {
  return useQuery({
    queryKey: ["chat", id],
    queryFn: ({ signal }) => api<ChatDetail>(`/assistent/chats/${id ?? ""}`, { signal }),
    enabled: Boolean(id) && abrufen,
    refetchInterval: (q) => (nachladen && offeneFrage(q.state.data) === "wartet" ? 3000 : false),
  });
}

export function useChatAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dokumentId?: string) =>
      api<Chat>("/assistent/chats", { json: dokumentId ? { dokument_id: dokumentId } : {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["chats"] }),
  });
}

export function useChatLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<unknown>(`/assistent/chats/${id}`, { methode: "DELETE" }),
    onSuccess: (_, id) => {
      // W73d (D-310): der geoeffnete Chat beobachtet seine Abfrage noch, bis die Seite weg ist; entfernt man sie vorher, baut
      // das naechste Rendern sie neu und holt den geloeschten Chat (404). Darum laufende Abrufe abbrechen und die Abfrage erst
      // entfernen, wenn ihr letzter Beobachter gegangen ist (aus dem Verlauf geloescht: sofort)
      const schluessel = ["chat", id];
      void qc.cancelQueries({ queryKey: schluessel, exact: true });
      const cache = qc.getQueryCache();
      const abfrage = cache.find({ queryKey: schluessel, exact: true });
      const entfernen = () => qc.removeQueries({ queryKey: schluessel, exact: true });
      if (!abfrage || abfrage.getObserversCount() === 0) entfernen();
      else {
        const ab = cache.subscribe((e) => {
          if (e.type !== "observerRemoved" || e.query !== abfrage || abfrage.getObserversCount() > 0) return;
          ab();
          entfernen();
        });
      }
      void qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });
}

// ---------------------------------------------------------------- Karten des Gaist (W80)

/** W80 (plan-w80.md 3.4): Koerper eines 409 von Bestaetigen oder Ablehnen mit dem aktuellen Stand der Karte, sonst null.
 *  Ohne lesbaren `vorschlag` (Kennung und Zustand als Text) bleibt es ein gewoehnlicher Fehler. */
export function vorschlagKonflikt(fehler: unknown): { detail: string; code: string; vorschlag: GaistVorschlag } | null {
  if (!(fehler instanceof ApiFehler) || fehler.status !== 409) return null;
  const d = fehler.daten as Record<string, unknown> | null;
  const v = d && typeof d === "object" ? (d.vorschlag as Record<string, unknown> | null | undefined) : null;
  if (!v || typeof v !== "object" || typeof v.id !== "string" || typeof v.zustand !== "string") return null;
  return { detail: fehler.message, code: typeof d?.code === "string" ? d.code : "", vorschlag: v as unknown as GaistVorschlag };
}

/** Ersetzt den Vorschlag im gespeicherten Chat (die Karte zeichnet sich aus dem Cache). */
function vorschlagErsetzen(qc: QueryClient, chatId: string, neu: GaistVorschlag) {
  qc.setQueryData<ChatDetail>(["chat", chatId], (alt) =>
    alt && {
      ...alt,
      nachrichten: alt.nachrichten.map((n) =>
        n.vorschlaege?.some((v) => v.id === neu.id) ? { ...n, vorschlaege: n.vorschlaege.map((v) => (v.id === neu.id ? neu : v)) } : n,
      ),
    },
  );
}

/** Nach „Ja“ kann sich alles geaendert haben, was die Karten anfassen, samt den Zaehlern der Navigation. */
function nachVorschlagNeuLaden(qc: QueryClient) {
  dokumenteNeuLaden(qc);
  for (const schluessel of ["aufgaben", "briefe", "brief", "erinnerungen"]) void qc.invalidateQueries({ queryKey: [schluessel] });
}

function useVorschlagEntscheiden(chatId: string, weg: "bestaetigen" | "ablehnen") {
  const qc = useQueryClient();
  const uebernehmen = async (v: GaistVorschlag) => {
    // ein Abruf des Chats, der vor der Antwort losging, wuerde den alten Stand zurueckschreiben
    await qc.cancelQueries({ queryKey: ["chat", chatId], exact: true });
    vorschlagErsetzen(qc, chatId, v);
    if (v.zustand === "bestaetigt") nachVorschlagNeuLaden(qc);
  };
  return useMutation({
    mutationFn: (vorschlagId: string) => api<GaistVorschlag>(`/assistent/vorschlaege/${vorschlagId}/${weg}`, { methode: "POST" }),
    // Offline pausiert TanStack Query Mutationen im Standardmodus („online“): die Karte bliebe bei „Wird ausgeführt …“ mit
    // gesperrten Knöpfen und der Tipp feuerte erst beim Wiederverbinden von selbst. Die Karte soll sofort „Keine
    // Verbindung“ zeigen (plan-w80.md 3.7), darum immer senden.
    networkMode: "always",
    onSuccess: uebernehmen,
    onError: async (fehler) => {
      const konflikt = vorschlagKonflikt(fehler);
      if (konflikt) await uebernehmen(konflikt.vorschlag);
    },
  });
}

/** „Ja“ an einer Karte: POST ohne Koerper (CSRF wie alle); 200 und 409 mit `vorschlag` zeichnen die Karte neu. */
export function useVorschlagBestaetigen(chatId: string) {
  return useVorschlagEntscheiden(chatId, "bestaetigen");
}

/** „Nein“ an einer Karte. */
export function useVorschlagAblehnen(chatId: string) {
  return useVorschlagEntscheiden(chatId, "ablehnen");
}

/** W80: geplante Erinnerungen des Nutzers. Ein Server ohne diese Route (404) hat keine: leere Liste, kein Fehler. */
export function useErinnerungen() {
  return useQuery({
    queryKey: ["erinnerungen"],
    queryFn: async ({ signal }) => {
      try {
        return await api<Erinnerung[]>("/erinnerungen", { signal });
      } catch (fehler) {
        if (fehler instanceof ApiFehler && fehler.status === 404) return [];
        throw fehler;
      }
    },
  });
}

export function useErinnerungLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<undefined>(`/erinnerungen/${id}`, { methode: "DELETE" }),
    // die Zeile geht sofort, nicht erst mit dem naechsten Abruf
    onSuccess: (_, id) => qc.setQueryData<Erinnerung[]>(["erinnerungen"], (alt) => alt?.filter((e) => e.id !== id)),
    onSettled: () => qc.invalidateQueries({ queryKey: ["erinnerungen"] }),
  });
}

// ---------------------------------------------------------------- Dokument-Werkzeuge (W7)

/** Startet ein Werkzeug am Dokument; die Arbeit macht der Worker (Auftrag abfragen). */
export function useWerkzeug(dokumentId: string) {
  return useMutation({
    mutationFn: (w: Werkzeug) => api<Auftrag>(`/dokumente/${dokumentId}/werkzeuge`, { json: w }),
  });
}

export function useZusammenfuegen() {
  return useMutation({
    mutationFn: (dokument_ids: string[]) => api<Auftrag>("/werkzeuge/zusammenfuegen", { json: { dokument_ids } }),
  });
}

/** Stand eines Auftrags; fragt jede Sekunde nach, bis er fertig ist oder scheitert. Danach laedt
 *  alles neu, was sich geaendert haben kann (Fassung, Text, neue Dokumente). */
export function useAuftrag(id: string | null) {
  const qc = useQueryClient();
  const abfrage = useQuery({
    queryKey: ["auftrag", id],
    queryFn: ({ signal }) => api<Auftrag>(`/werkzeuge/auftraege/${id}`, { signal }),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data && ["fertig", "fehler"].includes(q.state.data.status) ? false : 1000),
  });
  const status = abfrage.data?.status;
  useEffect(() => {
    if (status === "fertig") {
      dokumenteNeuLaden(qc);
      void qc.invalidateQueries({ queryKey: ["briefe"] });
      void qc.invalidateQueries({ queryKey: ["brief"] });
      void qc.invalidateQueries({ queryKey: ["formular"] });
      void qc.invalidateQueries({ queryKey: ["signaturen"] });
    }
  }, [status, qc]);
  return abfrage;
}

export function useFormular(dokumentId: string, aktiv: boolean) {
  return useQuery({
    queryKey: ["formular", dokumentId],
    queryFn: ({ signal }) => api<Formularfeld[]>(`/dokumente/${dokumentId}/formular`, { signal }),
    enabled: aktiv,
  });
}

export function useSignaturen(dokumentId: string, aktiv: boolean) {
  return useQuery({
    queryKey: ["signaturen", dokumentId],
    queryFn: ({ signal }) => api<SignaturBefund[]>(`/dokumente/${dokumentId}/signaturen`, { signal }),
    enabled: aktiv,
  });
}

export function useUnterschrift() {
  return useQuery({
    queryKey: ["unterschrift"],
    queryFn: ({ signal }) => api<Unterschrift | null>("/unterschrift", { signal }),
  });
}

export function useUnterschriftSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: ({ art: "gezeichnet"; bild: string } | { art: "getippt"; name: string }) & { signal?: AbortSignal }) =>
      api<Unterschrift>("/unterschrift", { methode: "PUT", json: daten, signal }),
    onSuccess: (u) => {
      qc.setQueryData(["unterschrift"], u);
      void qc.invalidateQueries({ queryKey: ["unterschrift-bild"] });
    },
  });
}

export function useUnterschriftLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<undefined>("/unterschrift", { methode: "DELETE" }),
    onSuccess: () => qc.setQueryData(["unterschrift"], null),
  });
}

export function useVersenden(dokumentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (daten: { an: string; betreff: string; text: string; sichtbar_fuer: Sichtbarkeit }) =>
      api<Versand>(`/dokumente/${dokumentId}/versenden`, { json: daten }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["versand", dokumentId] }),
  });
}

/** Nur der Absender: wer den Versand in der Liste des Dokuments sieht. */
export function useVersandSichtbarkeit(dokumentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (daten: { id: string; sichtbar_fuer: Sichtbarkeit }) =>
      api<Versand>(`/versand/${daten.id}`, { methode: "PATCH", json: { sichtbar_fuer: daten.sichtbar_fuer } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["versand", dokumentId] }),
  });
}

export function useVersandListe(dokumentId: string, aktiv = true) {
  return useQuery({
    queryKey: ["versand", dokumentId],
    queryFn: ({ signal }) => api<Versand[]>(`/dokumente/${dokumentId}/versand`, { signal }),
    enabled: aktiv,
    refetchInterval: (q) => (q.state.data?.some((v) => v.status === "wartet") ? 2000 : false),
  });
}

// ---------------------------------------------------------------- Briefe (W7)

export function useVorlagen() {
  return useQuery({
    queryKey: ["vorlagen"],
    queryFn: ({ signal }) => api<Vorlage[]>("/briefe/vorlagen", { signal }),
    staleTime: Infinity,
  });
}

/** W75: mit `dokumentId` nur der eigene Brief zu einem abgelegten Dokument („Wie weiter?“ am Dokument). */
export function useBriefe(optionen: { dokumentId?: string; aktiv?: boolean } = {}) {
  const { dokumentId, aktiv = true } = optionen;
  return useQuery({
    queryKey: dokumentId ? ["briefe", { dokumentId }] : ["briefe"],
    queryFn: ({ signal }) => api<Brief[]>(dokumentId ? `/briefe?dokument_id=${encodeURIComponent(dokumentId)}` : "/briefe", { signal }),
    enabled: aktiv,
  });
}

export function useBrief(id: string | undefined) {
  return useQuery({
    queryKey: ["brief", id],
    queryFn: ({ signal }) => api<Brief>(`/briefe/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function useBriefAnlegen() {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: (daten: { vorlage: string; bezug_id?: string; akte_id?: string }) => mitGrenze((signal) => api<Brief>("/briefe", { json: daten, signal })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["briefe"] }),
  });
}

export type BriefFelder = Partial<Pick<Brief, "absender" | "empfaenger" | "ort" | "datum" | "betreff" | "text">>;

/** W75 (D-287): Absender als Anschrift (ausgeschaltetes Telefon oder Mail fehlt), nie zusammen mit `absender` (422);
 *  `im_profil_speichern` ergaenzt die Person, loescht dort nie Telefon oder Mail (D-319). */
export type BriefAbsender = { absender_anschrift: Partial<Anschrift>; absender_person_id: string | null; im_profil_speichern: boolean };

export function useBriefAendern(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: (BriefFelder | BriefAbsender) & { signal?: AbortSignal }) => api<Brief>(`/briefe/${id}`, { methode: "PATCH", json: daten, signal }),
    onSuccess: (b, daten) => {
      qc.setQueryData(["brief", id], b);
      void qc.invalidateQueries({ queryKey: ["briefe"] });
      // „Im Profil speichern“ aendert die Person mit
      if ("im_profil_speichern" in daten && daten.im_profil_speichern) void qc.invalidateQueries({ queryKey: ["personen"] });
    },
  });
}

/** W75 (S4, D-297): die eigene gespeicherte Unterschrift am Unterschriftsplatz des abgelegten Briefs (Auftrag). */
export function useBriefUnterschreiben(id: string) {
  return useMutation({
    // W82 Web-11a W11A-4 (R1 Web-10c H-3): Grenze 10 s wie K47 (`mitGrenzeErkannt`); ohne Antwort scheitert der Tipp mit TimeoutError
    mutationFn: () => mitGrenzeErkannt((grenze) => api<Auftrag>(`/briefe/${id}/unterschreiben`, { methode: "POST", signal: grenze })),
  });
}

/** W82 Web-5h2 (K43, Server 5h-S S6): Hinweise des Servers zur Vorschau des gesicherten Stands (Anschrift im Fenster, Absender
 *  zu lang, Rücksendeangabe, Zeichen), wortgleich aus `briefpdf.maengel`. `stand` ist `geaendert` des gesicherten Entwurfs,
 *  ohne ihn keine Abfrage. Jeder Fehler (ältere Server ohne Route 404, fertiger Brief 409, Grenze 429, 503, Netz) heißt: keine
 *  Hinweise, die Vorschau öffnet wie bisher. */
export function useBriefHinweise(id: string, stand: string | null) {
  const anfrage = useQuery({
    queryKey: ["brief-hinweise", id, stand],
    queryFn: stand
      ? async ({ signal }) => {
          try {
            const antwort = await api<{ hinweise?: unknown }>(`/briefe/${id}/hinweise`, { signal });
            return Array.isArray(antwort.hinweise) ? antwort.hinweise.filter((h): h is string => typeof h === "string" && h.trim() !== "") : [];
          } catch {
            return [];
          }
        }
      : skipToken,
    retry: false,
    staleTime: Infinity,
  });
  return anfrage.data ?? [];
}

/** W75 (S4): Vorschau des gesicherten Entwurfs; `geaendert` macht jede gesicherte Fassung zu einer eigenen Adresse. */
export function briefVorschauUrl(id: string, geaendert: string): string {
  return `/api/briefe/${id}/vorschau?v=${encodeURIComponent(geaendert)}`;
}

/** W73e (S1, D-380 HB8): das PDF des gesicherten Stands als Datei fuer das Blatt „Wie weiter?“, ohne etwas abzulegen.
 *  `pruefen=1`: der Server misst die Maengel wie beim Fertigstellen und antwortet dann 422 mit seinem Satz. Nicht `ok`
 *  wird ein `ApiFehler` wie in `api()`, ohne Netz bleibt es der `TypeError`. */
export async function briefPdf(id: string, geaendert: string, betreff: string): Promise<File> {
  const antwort = await fetch(`${briefVorschauUrl(id, geaendert)}&pruefen=1`, { credentials: "same-origin" });
  if (!antwort.ok) {
    const daten: unknown = await antwort.json().catch(() => null);
    throw new ApiFehler(antwort.status, meldung(antwort.status, daten), daten);
  }
  return new File([await antwort.blob()], dateiname(betreff), { type: "application/pdf" });
}

export function useBriefLoeschen() {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: (id: string) => mitGrenze((signal) => api<undefined>(`/briefe/${id}`, { methode: "DELETE", signal })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["briefe"] }),
  });
}

export function useBriefEntwurf(id: string) {
  return useMutation({
    mutationFn: (anliegen: string) => api<{ betreff: string; text: string }>(`/briefe/${id}/entwurf`, { json: { anliegen } }),
  });
}

export function useBriefFertig(id: string) {
  return useMutation({
    mutationFn: () => api<Auftrag>(`/briefe/${id}/fertig`, { methode: "POST" }),
  });
}

// ---------------------------------------------------------------- Post per Mail (W8)

export function useEingang() {
  return useQuery({ queryKey: ["eingang"], queryFn: ({ signal }) => api<Eingang>("/eingang", { signal }) });
}

export function useEingangAdresseNeu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<Eingang>("/eingang/adresse", { methode: "POST" }),
    onSuccess: (e) => qc.setQueryData(["eingang"], e),
  });
}

export function useEingangAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (daten: { nur_bekannte?: boolean; aktiv?: boolean }) => api<Eingang>("/eingang", { methode: "PATCH", json: daten }),
    onSuccess: (e) => qc.setQueryData(["eingang"], e),
  });
}

/** Eingegangene Mails; solange eine verarbeitet wird, alle 3 s neu. `live`: auf der Seite
 *  „Post per Mail“ auch sonst alle 10 s (eine weitergeleitete Mail erscheint ohne Neuladen). */
export function useEingangMails(status: "zweifel" | "alle" = "alle", live = false) {
  return useQuery({
    queryKey: ["eingang-mails", status],
    queryFn: ({ signal }) => api<EingangMail[]>(`/eingang/mails?status=${status}`, { signal }),
    refetchInterval: (q) => (q.state.data?.some((m) => m.status === "wartet") ? 3000 : live ? 10_000 : false),
  });
}

function mailsNeu(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["eingang-mails"] });
  void qc.invalidateQueries({ queryKey: ["eingang-absender"] });
  void qc.invalidateQueries({ queryKey: ["dokumente"] });
}

export function useMailUebernehmen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; erlauben: boolean }) => api<EingangMail>(`/eingang/mails/${p.id}/uebernehmen`, { json: { erlauben: p.erlauben } }),
    onSuccess: () => mailsNeu(qc),
  });
}

export function useMailVerwerfen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; sperren: boolean }) => api<EingangMail>(`/eingang/mails/${p.id}/verwerfen`, { json: { sperren: p.sperren } }),
    onSuccess: () => mailsNeu(qc),
  });
}

export function useAbsenderRegeln() {
  return useQuery({ queryKey: ["eingang-absender"], queryFn: ({ signal }) => api<AbsenderRegel[]>("/eingang/absender", { signal }) });
}

export function useAbsenderRegelSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: { adresse: string; art: "erlaubt" | "gesperrt"; signal?: AbortSignal }) => api<AbsenderRegel>("/eingang/absender", { json: daten, signal }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["eingang-absender"] }),
  });
}

export function useAbsenderRegelLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<undefined>(`/eingang/absender/${id}`, { methode: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["eingang-absender"] }),
  });
}

export function useImapKonten() {
  return useQuery({
    queryKey: ["imap"],
    queryFn: ({ signal }) => api<ImapKonto[]>("/eingang/imap", { signal }),
    // nach Anlegen oder „Jetzt abrufen“ kommt das Ergebnis vom Worker; W82 Web-10b (L-5): im Erstabruf mit Zahl alle 30 s
    refetchInterval: (q) => imapTakt(q.state.data),
  });
}

/** W82 Web-10b W10B-1 (S98-1): Anbieterliste für Gruppe und Blatt. `null` heißt: keine gültige Liste (alter Server mit 405
 *  oder 404, jeder andere Fehler, andere Form), dann bleibt alles wie bisher. Kein zweiter Versuch: der Rückfall soll gleich
 *  stehen. Keine Adresse in der Anfrage, den Vorschlag rechnet `anbieterVorschlag`. */
export function useMailAnbieter() {
  return useQuery({
    queryKey: ["imap-anbieter"],
    queryFn: ({ signal }) => api<unknown>("/eingang/imap/anbieter", { signal }),
    select: anbieterListeGueltig,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** `altbriefe_einordnen` (S98-5 (3)) nur, wenn die Liste gültig war; ein alter Server lehnt ein unbekanntes Feld mit 422 ab. */
export function useImapAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...daten }: { host: string; benutzer: string; passwort: string; ordner: string; altbriefe_einordnen?: boolean; signal?: AbortSignal }) =>
      api<ImapKonto>("/eingang/imap", { json: daten, signal }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["imap"] }),
  });
}

export function useImapAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; aktiv?: boolean; passwort?: string; ordner?: string; altbriefe_einordnen?: boolean }) => {
      const { id, ...daten } = p;
      return api<ImapKonto>(`/eingang/imap/${id}`, { methode: "PATCH", json: daten });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["imap"] }),
  });
}

export function useImapLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<undefined>(`/eingang/imap/${id}`, { methode: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["imap"] }),
  });
}

export function useImapAbrufen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<ImapKonto>(`/eingang/imap/${id}/abrufen`, { methode: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["imap"] });
      void qc.invalidateQueries({ queryKey: ["eingang-mails"] });
    },
  });
}

// ---------------------------------------------------------------- Produktreife (W9)

export function usePlan() {
  return useQuery({ queryKey: ["plan"], queryFn: ({ signal }) => api<PlanStand>("/plan", { signal }) });
}

export function usePlanWechseln() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (plan: PlanName) => api<PlanStand>("/plan", { json: { plan } }),
    onSuccess: (p) => {
      qc.setQueryData(["plan"], p);
      void qc.invalidateQueries({ queryKey: ["ich"] });
    },
  });
}

export function useEinfuehrungGesehen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<undefined>("/konto/einfuehrung", { methode: "POST" }),
    onSuccess: () => qc.setQueryData<Ich | null>(["ich"], (ich) => (ich ? { ...ich, eingefuehrt: true } : ich)),
  });
}

/** Exporte; solange einer entsteht, alle 2 s neu. */
export function useExporte() {
  return useQuery({
    queryKey: ["exporte"],
    queryFn: ({ signal }) => api<Export[]>("/konto/exporte", { signal }),
    refetchInterval: (q) => (q.state.data?.some((e) => e.status === "wartet" || e.status === "laeuft") ? 2000 : false),
  });
}

export function useExportAnfordern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<Export>("/konto/exporte", { methode: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exporte"] }),
  });
}

export function useExportLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<undefined>(`/konto/exporte/${id}`, { methode: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exporte"] }),
  });
}

/** Abmelden und Konto loeschen: Sitzung, Daten und gemerkten Rueckweg (W20g) verwerfen. */
function abgemeldet(qc: QueryClient) {
  bewusstAbgemeldet = true;
  setzeCsrf("");
  zurueckVergessen();
  // W82 Web-6b (ZP WN-31): zuerst `ich` leeren, dann den Speicher. Eingehängte Beobachter (die Routen-Wache in App.tsx) hängen
  // nach `clear` an der entfernten Abfrage und erführen sonst nichts: ohne eigene Navigation der Seite (Toast „Abmelden“, späte
  // Bestätigung nach einem zweiten Versuch) bliebe das Konto sichtbar, obwohl der Server abgemeldet hat
  qc.setQueryData(["ich"], null);
  qc.clear();
  qc.setQueryData(["ich"], null);
}

export function useKontoLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    // ohne Passwort am Konto (W20b) faellt das Feld weg; dann zaehlt die frische Anmeldung per Link
    mutationFn: (daten: { passwort?: string; haushalt_loeschen: boolean }) =>
      api<{ haushalt: boolean }>("/konto/loeschen", { json: daten }),
    onSuccess: () => abgemeldet(qc),
  });
}

export function useAudit() {
  return useInfiniteQuery({
    queryKey: ["audit"],
    queryFn: ({ pageParam, signal }) =>
      api<AuditSeite>(`/audit?anzahl=50${pageParam ? `&vor=${pageParam}` : ""}`, { signal }),
    initialPageParam: 0,
    getNextPageParam: (letzte) => letzte.weiter ?? undefined,
  });
}

export function useMitglieder() {
  return useQuery({ queryKey: ["mitglieder"], queryFn: ({ signal }) => api<Mitglied[]>("/haushalt/mitglieder", { signal }) });
}

export function useEinladungen(aktiv: boolean) {
  return useQuery({
    queryKey: ["einladungen"],
    queryFn: ({ signal }) => api<Einladung[]>("/haushalt/einladungen", { signal }),
    enabled: aktiv,
  });
}

function haushaltNeu(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ["mitglieder"] });
  void qc.invalidateQueries({ queryKey: ["einladungen"] });
  void qc.invalidateQueries({ queryKey: ["plan"] });
  void qc.invalidateQueries({ queryKey: ["personen"] });
}

export function useEinladen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, signal }: { email: string; signal?: AbortSignal }) => api<Einladung>("/haushalt/einladungen", { json: { email }, signal }),
    onSuccess: () => haushaltNeu(qc),
  });
}

export function useEinladungZurueckziehen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<undefined>(`/haushalt/einladungen/${id}`, { methode: "DELETE" }),
    onSuccess: () => haushaltNeu(qc),
  });
}

export function useMitgliedEntfernen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<undefined>(`/haushalt/mitglieder/${id}`, { methode: "DELETE" }),
    onSuccess: () => haushaltNeu(qc),
  });
}

export function useInhaberUebergeben() {
  const qc = useQueryClient();
  return useMutation({
    // ohne Passwort am Konto (W20b) faellt das Feld weg; dann zaehlt die frische Anmeldung per Link
    mutationFn: (daten: { nutzer_id: string; passwort?: string }) => api<Mitglied[]>("/haushalt/inhaber", { json: daten }),
    onSuccess: () => {
      haushaltNeu(qc);
      void qc.invalidateQueries({ queryKey: ["ich"] });
    },
  });
}

export function useHaushaltUmbenennen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, signal }: { name: string; signal?: AbortSignal }) => api<Haushalt>("/haushalt", { methode: "PATCH", json: { name }, signal }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ich"] }),
  });
}

// ---------------------------------------------------------------- Links aus Mails (W9)

export function usePasswortVergessen() {
  return useMutation({ mutationFn: (email: string) => api<{ ok: boolean }>("/auth/passwort-vergessen", { json: { email } }) });
}

export function usePasswortNeu() {
  return useMutation({
    mutationFn: (daten: { token: string; passwort: string }) => api<undefined>("/auth/passwort-neu", { json: daten }),
  });
}

export function useEinladungAnnehmen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (daten: { token: string; passwort?: string; name: string; einwilligung: boolean; fassung: string; nutzung_aus?: boolean }) =>
      api<Ich>("/auth/einladung-annehmen", { json: daten }),
    onSuccess: (ich, daten) => {
      setzeCsrf(ich.csrf);
      bewusstAbgemeldet = false;
      qc.clear();
      qc.setQueryData(["ich"], mitWahl(ich, daten.nutzung_aus));
    },
  });
}

/** W82 Web-5b (K4, F2 = A): ohne Sitzung schickt die Seite `anmelden: true`; beim ersten Bestätigen antwortet der Server dann
 *  200 `{ anmeldelink }` (einlösen erst auf „Jetzt anmelden“), sonst 204 (Antwort `undefined`). Danach fällt der Merker
 *  `pg-registriert`. */
export function useMailBestaetigen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (daten: { token: string; anmelden: boolean }) =>
      api<{ anmeldelink?: string } | undefined>("/auth/bestaetigen", { json: daten.anmelden ? { token: daten.token, anmelden: true } : { token: daten.token } }),
    onSuccess: () => {
      registriertVergessen();
      qc.setQueryData<Ich | null>(["ich"], (ich) => (ich ? { ...ich, bestaetigt: true } : ich));
    },
  });
}

export function useBestaetigungSenden() {
  return useMutation({ mutationFn: () => api<{ ok: boolean }>("/auth/bestaetigung-senden", { methode: "POST" }) });
}


// ---------------------------------------------------------------- W12b Lernende Sortierung

export function useKennenlernen() {
  return useQuery({
    queryKey: ["kennenlernen"],
    queryFn: ({ signal }) => api<Kennenlernen>("/kennenlernen", { signal }),
    // Neue Briefe werden gerade erkannt: Karten kommen nach und nach dazu
    refetchInterval: (q) => (q.state.data?.aktiv && q.state.data.karten.length === 0 ? 5000 : false),
  });
}

function kennenlernenNeuLaden(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["kennenlernen"] });
  void qc.invalidateQueries({ queryKey: ["gelernt"] });
  dokumenteNeuLaden(qc);
}

export function useKennenlernenWeitere() {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: () => mitGrenze((signal) => api<Kennenlernen>("/kennenlernen/weitere", { methode: "POST", signal })),
    onSuccess: () => kennenlernenNeuLaden(qc),
  });
}

export function useKennenlernenUeberspringen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<Kennenlernen>("/kennenlernen/ueberspringen", { methode: "POST" }),
    onSuccess: () => kennenlernenNeuLaden(qc),
  });
}

export function useGelernt() {
  return useQuery({
    queryKey: ["gelernt"],
    queryFn: ({ signal }) => api<GelerntAbsender[]>("/gelernt", { signal }),
  });
}

export function useVergessen() {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: (absenderId: string | null) =>
      mitGrenze((signal) => api<undefined>(absenderId ? `/gelernt/${absenderId}` : "/gelernt", { methode: "DELETE", signal })),
    onSuccess: () => kennenlernenNeuLaden(qc),
  });
}

export function useAuswertung() {
  return useQuery({
    queryKey: ["auswertung"],
    queryFn: ({ signal }) => api<{ aus: boolean }>("/konto/auswertung", { signal }),
  });
}

/** W15: Nutzungsbericht fuer den Betreiber (fuer alle anderen antwortet der Server mit 404). */
export function useNutzungsbericht(tage: number) {
  return useQuery({
    queryKey: ["betrieb-nutzung", tage],
    queryFn: ({ signal }) => api<Nutzungsbericht>(`/betrieb/nutzung${query({ tage: String(tage) })}`, { signal }),
  });
}

/** W82 Beta-W (Vertrag Beta-S): Rückmeldung senden (`POST /api/rueckmeldungen`, multipart). Die Zeitgrenze gehört dem Blatt
 *  (`sichern(…, grenzeMs)`: 10 s ohne, 30 s mit Bildschirmfoto); immer senden, auch ohne Netz (sofort „Keine Verbindung“, nach
 *  dem Wiederverbinden geht nichts von selbst los). */
export function useRueckmeldungSenden() {
  return useMutation({
    mutationFn: ({ formular, signal }: { formular: FormData; signal?: AbortSignal }) => api<RueckmeldungAntwort>("/rueckmeldungen", { formular, signal }),
    networkMode: "always",
  });
}

/** W82 Beta-W: Rückmeldungen für den Betreiber (für jedes andere Konto antwortet der Server mit 404 „Not Found“). */
export function useBetriebRueckmeldungen(status: "offen" | "erledigt") {
  return useQuery({
    queryKey: ["betrieb-rueckmeldungen", status],
    queryFn: ({ signal }) => api<RueckmeldungBetrieb[]>(`/betrieb/rueckmeldungen${query({ status })}`, { signal }),
    retry: (n, fehler) => !(fehler instanceof ApiFehler && fehler.status < 500) && n < 2,
  });
}

/** W82 Web-5h-N2 a9 (H9): die Mutation wartet nicht auf das Neuladen der Liste (ein hängender Abruf hielt sonst die Rückfrage
 *  gesperrt, ohne Grenze); die Rückmeldung verlässt die Liste sofort im Cache (`aus`), danach lädt die Liste im Hintergrund. */
function rueckmeldungenNeuLaden(qc: QueryClient, id: string, aus: ("offen" | "erledigt")[]) {
  for (const status of aus) qc.setQueryData<RueckmeldungBetrieb[]>(["betrieb-rueckmeldungen", status], (alt) => alt?.filter((r) => r.id !== id));
  void qc.invalidateQueries({ queryKey: ["betrieb-rueckmeldungen"] });
}

/** W82 Beta-W: „Erledigt“ (Bild weg, Text 30 Tage unter „Erledigt“); Grenze 10 s wie jede Mutation ohne Blatt (K47). */
export function useRueckmeldungErledigen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mitGrenze((signal) => api<RueckmeldungBetrieb>(`/betrieb/rueckmeldungen/${encodeURIComponent(id)}/erledigt`, { methode: "POST", signal })),
    networkMode: "always",
    onSuccess: (_, id) => rueckmeldungenNeuLaden(qc, id, ["offen"]),
  });
}

/** W82 Beta-W: „Löschen“ (204, Text und Bild sofort weg); Grenze 10 s (K47). */
export function useRueckmeldungLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mitGrenze((signal) => api<undefined>(`/betrieb/rueckmeldungen/${encodeURIComponent(id)}`, { methode: "DELETE", signal })),
    networkMode: "always",
    onSuccess: (_, id) => rueckmeldungenNeuLaden(qc, id, ["offen", "erledigt"]),
  });
}

/** W15: Widerspruch gegen die Nutzungsstatistik (welche Bildschirme und Funktionen genutzt werden). */
export function useNutzungSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ aus, signal }: { aus: boolean; signal?: AbortSignal }) => api<undefined>("/nutzung/einstellung", { methode: "PUT", json: { aus }, signal }),
    // die Statistik selbst folgt dem Stand von „ich“ (main.tsx); W77: der Server schliesst dabei den Hinweis
    onSuccess: (_, { aus }) => {
      qc.setQueryData<Ich | null>(["ich"], (ich) => (ich ? { ...ich, nutzer: { ...ich.nutzer, nutzung_aus: aus, nutzung_hinweis_offen: false } } : ich));
    },
  });
}

export function useAuswertungSetzen() {
  const qc = useQueryClient();
  return useMutation({
// W82 Web-5h1 (K47): Grenze 10 s (`mitGrenze`), ohne Antwort „Keine Verbindung. Bitte später noch einmal.“
    mutationFn: (aus: boolean) => mitGrenze((signal) => api<{ aus: boolean }>("/konto/auswertung", { methode: "PUT", json: { aus }, signal })),
    onSuccess: (daten) => qc.setQueryData(["auswertung"], daten),
  });
}
